import { RSAA } from 'redux-api-middleware';
import { Actions } from '../actionTypes/actionTypes';
import type { RSAACallRequest } from '@makemydeal/dr-react-18-wrappers';
import type { Selectors } from '../common/rootSelectors';
import { buildUpdateDealPayload, getSalesCheckinBffEndPoint } from '@makemydeal/dr-sales-checkin-ui-utils';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { GetState, SalesCheckinRootState } from '../common/types/SalesCheckinRootState';
import type { DealVersionChangeType } from '@makemydeal/dr-shared-bff-types';

/**
 * Fetches all deals for a customer by their consumer ID (ccid) using RSAA.
 * Dispatches FETCH_DEALS_REQUEST → FETCH_DEALS_SUCCESS | FETCH_DEALS_FAILURE,
 * which the customerVehicles reducer and customerVehiclesMiddleware already handle
 * (deal list update + badge count sync via the FETCH_DEALS_SUCCESS case).
 */
export const fetchCustomerVehicleDealsByCcid =
    (selectors: Selectors, consumerId: string) => (dispatch: any, getState: GetState) => {
        const state: SalesCheckinRootState = getState();
        const environment = selectors.getEnvironment(state);
        const endpoint = getSalesCheckinBffEndPoint(`deals/ccid/${consumerId}`, environment);
        const request: RSAACallRequest = {
            endpoint,
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            types: [
                Actions.CUSTOMER_VEHICLES_FETCH_DEALS_REQUEST,
                {
                    type: Actions.CUSTOMER_VEHICLES_FETCH_DEALS_SUCCESS,
                    payload: async (_action: AnyFSA, _state: any, res: Response) => {
                        const data = await res.json();
                        return { deals: data?.data?.deals ?? [] };
                    }
                },
                Actions.CUSTOMER_VEHICLES_FETCH_DEALS_FAILURE
            ],
            fetch: async (...args) => await fetch(...args)
        };
        dispatch({ [RSAA]: request });
    };

/**
 * @param meta forwarded onto UPDATE_DEAL_SUCCESS so a caller can express intent that must only take
 * effect once the save lands (see `navigateOnSuccess` on `updateDealAction`).
 * @param changeType identifies which page triggered the save, so the BFF can write the correct
 * per-page version comment.
 */
export const updateDeal =
    (selectors: Selectors, meta?: Record<string, unknown>, changeType?: DealVersionChangeType) =>
    (dispatch: any, getState: GetState) => {
        const state = getState();
        const bffSaveDealEndpoint = getSalesCheckinBffEndPoint('deals', selectors.getEnvironment(state));
        const payload = buildUpdateDealPayload(state, changeType);
        const request: RSAACallRequest = {
            endpoint: bffSaveDealEndpoint,
            method: 'PUT',
            body: JSON.stringify(payload),
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            types: [
                { type: Actions.UPDATE_DEAL_REQUEST, meta: { changeType } },
                { type: Actions.UPDATE_DEAL_SUCCESS, meta },
                Actions.UPDATE_DEAL_FAILURE
            ],
            fetch: async (...args) => await fetch(...args)
        };
        dispatch({ [RSAA]: request });
    };
